import type { ColumnType } from "@similarweb/pro-logic-components";
import { ChangeCell, IconCell, IntentCell, tableSimpleSort } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberSupportNegativeFilter, percentageSignFilter } from "app/filters/ngFilters";
import { SourceBadge } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/source-badge/SourceBadge";
import type { TopicSourceType } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/source-badge/types";
import { StatusIntent } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/StatusIntent";
import { TopBrandsCell } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/TopBrandsCell";
import type { Prompt } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/types";
import { ChangeStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/types";
import {
    getCitationGapStatusConfig,
    getSentimentStatusConfig,
    getVolatilityStatusConfig,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import {
    citationGapStatusSorter,
    emptyLastModifier,
    sentimentStatusSorter,
    volatilityStatusSorter,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ISortingData } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/tableReducer";
import type {
    CitationGapStatus,
    VolatilityStatus,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { SentimentStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

import { CitationsCell } from "./CitationsCell";
import { COLUMN_KEYS } from "./consts";
import { PromptCell } from "./PromptCell";

const topBrandsSort = (a: Prompt, b: Prompt, sortOrder?: string | null) => {
    const labelA = a.topBrands?.[0]?.name;
    const labelB = b.topBrands?.[0]?.name;
    if (labelA && labelB) {
        return labelA.localeCompare(labelB);
    }
    if (!labelA && !labelB) {
        return 0;
    }
    return (labelA ? -1 : 1) * emptyLastModifier(sortOrder);
};

const topSourceCategoriesSort = (a: Prompt, b: Prompt, sortOrder?: string | null) => {
    const labelA = a.topSourceCategories?.[0]?.category;
    const labelB = b.topSourceCategories?.[0]?.category;
    if (labelA && labelB) {
        return labelA.localeCompare(labelB);
    }
    if (!labelA && !labelB) {
        return 0;
    }
    return (labelA ? -1 : 1) * emptyLastModifier(sortOrder);
};

const topicsSort = (a: Prompt, b: Prompt, sortOrder?: string | null) => {
    const topicA = a.topics?.[0];
    const topicB = b.topics?.[0];
    if (topicA && topicB) {
        return topicA.localeCompare(topicB);
    }
    if (!topicA && !topicB) {
        return 0;
    }
    return (topicA ? -1 : 1) * emptyLastModifier(sortOrder);
};

const visibilityAndPromptSort = () => {
    return (a: Prompt, b: Prompt) => {
        const visibilityDiff = (a.visibility ?? 0) - (b.visibility ?? 0);
        if (visibilityDiff !== 0) {
            return visibilityDiff;
        }

        return (b.prompt ?? "").localeCompare(a.prompt ?? "");
    };
};

export const getColumnConfig = (
    sorting: ISortingData<keyof Prompt>,
    coreCompetitors?: string[],
): Array<ColumnType<Prompt>> => [
    {
        dataIndex: COLUMN_KEYS.prompt,
        title: translate("llm.prompts.column.prompt.title"),
        tooltipSubtitle: translate("llm.prompts.column.prompt.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.prompt ? sorting.sortDirection : undefined,
        sorter: tableSimpleSort(COLUMN_KEYS.prompt),
        width: 500,
        fixed: true,
        render: (prompt) => <PromptCell text={prompt} />,
    },
    {
        dataIndex: COLUMN_KEYS.visibility,
        title: translate("llm.prompts.column.visibility.title"),
        tooltipSubtitle: translate("llm.prompts.column.visibility.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.visibility ? sorting.sortDirection : undefined,
        sorter: visibilityAndPromptSort(),
        width: 100,
        align: "right",
        render: (visibility = 0) => (visibility === 0 ? visibility : percentageSignFilter()(visibility)),
    },
    {
        dataIndex: COLUMN_KEYS.popChange,
        title: translate("llm.prompts.column.pop_change.title"),
        tooltipSubtitle: translate("llm.prompts.column.pop_change.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.popChange ? sorting.sortDirection : undefined,
        sorter: true,
        width: 125,
        align: "right",
        render: (popChange, record) => {
            if (record.changeStatus === ChangeStatus.New) {
                return (
                    <IntentCell
                        label={translate("llm.prompts.column.pop_change.new")}
                        bgColor={colorsPalettes.green[100]}
                        textColor={colorsPalettes.green[500]}
                    />
                );
            }

            if (record.changeStatus === ChangeStatus.Lost) {
                return (
                    <IntentCell
                        label={translate("llm.prompts.column.pop_change.lost")}
                        bgColor={colorsPalettes.red[100]}
                        textColor={colorsPalettes.red[500]}
                    />
                );
            }

            if (popChange == null) {
                return NO_DATA_SIGN;
            }

            const formatted = abbrNumberSupportNegativeFilter()(popChange * 100, true, 2) as string;
            const isNegligible = Number(formatted) === 0;

            return (
                <ChangeCell
                    value={isNegligible ? 0 : popChange}
                    formattedValue={formatted.toString()}
                    noChangeSign={"0"}
                />
            );
        },
    },
    {
        dataIndex: COLUMN_KEYS.mentionSentimentStatus,
        title: translate("llm.prompts.column.latest_mention.title"),
        tooltipSubtitle: translate("llm.prompts.column.latest_mention.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.mentionSentimentStatus ? sorting.sortDirection : undefined,
        sorter: (a: Prompt, b: Prompt) => {
            const isMentionedA = a.mentionSentimentStatus !== SentimentStatus.NotMentioned ? 1 : 0;
            const isMentionedB = b.mentionSentimentStatus !== SentimentStatus.NotMentioned ? 1 : 0;
            return isMentionedA - isMentionedB;
        },
        width: 125,
        render: (mentionSentimentStatus: SentimentStatus) =>
            mentionSentimentStatus !== SentimentStatus.NotMentioned ? (
                <Tooltip content={translate("llm.prompts.column.latest_mention.cell_tooltip")}>
                    <IconCell
                        type="swReactIcon"
                        iconName="checked-circle"
                        iconColor={colorsPalettes.carbon[400]}
                        size="xs"
                    />
                </Tooltip>
            ) : null,
    },
    {
        dataIndex: COLUMN_KEYS.sentimentTone,
        title: translate("llm.prompts.column.sentiment_score.title"),
        tooltipSubtitle: translate("llm.prompts.column.sentiment_score.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.sentimentTone ? sorting.sortDirection : undefined,
        sorter: (a: Prompt, b: Prompt, sortOrder?: string | null) =>
            sentimentStatusSorter(a, b, COLUMN_KEYS.sentimentTone, sortOrder),
        width: 125,
        render: (sentimentTone?: SentimentStatus | null) =>
            sentimentTone !== SentimentStatus.NotMentioned ? (
                <StatusIntent
                    config={getSentimentStatusConfig()}
                    status={sentimentTone}
                    data-automation="prompts-sentiment-status"
                />
            ) : null,
    },
    {
        dataIndex: COLUMN_KEYS.topBrands,
        key: COLUMN_KEYS.topBrands,
        title: translate("llm.prompts.column.top_brands.title"),
        tooltipSubtitle: translate("llm.prompts.column.top_brands.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.topBrands ? sorting.sortDirection : undefined,
        sorter: topBrandsSort,
        width: 150,
        shouldCellUpdate: () => true,
        render: (topBrands) => (
            <TopBrandsCell
                topBrands={topBrands ?? []}
                coreCompetitors={coreCompetitors}
            />
        ),
    },
    {
        dataIndex: COLUMN_KEYS.topSourceCategories,
        title: translate("llm.prompts.column.top_source_category.title"),
        tooltipSubtitle: translate("llm.prompts.column.top_source_category.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.topSourceCategories ? sorting.sortDirection : undefined,
        sorter: topSourceCategoriesSort,
        width: 150,
        render: (topSourceCategories) =>
            topSourceCategories?.length ? (
                <SourceBadge
                    sources={topSourceCategories.map(({ category, share }) => ({
                        name: category as TopicSourceType,
                        share,
                    }))}
                />
            ) : (
                NO_DATA_SIGN
            ),
    },
    {
        dataIndex: COLUMN_KEYS.citationsWithInfluence,
        title: translate("llm.prompts.column.citations.title"),
        tooltipSubtitle: translate("llm.prompts.column.citations.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.citationsWithInfluence ? sorting.sortDirection : undefined,
        sorter: (a: Prompt, b: Prompt) =>
            (a.citationsWithInfluence?.length ?? 0) - (b.citationsWithInfluence?.length ?? 0),
        width: 125,
        render: (citations) => <CitationsCell citations={citations ?? []} />,
    },
    {
        dataIndex: COLUMN_KEYS.isCitedLatest,
        title: translate("llm.prompts.column.latest_citation_status.title"),
        tooltipSubtitle: translate("llm.prompts.column.latest_citation_status.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.isCitedLatest ? sorting.sortDirection : undefined,
        sorter: tableSimpleSort(COLUMN_KEYS.isCitedLatest),
        width: 150,
        hidden: true,
        render: (isCitedLatest) =>
            isCitedLatest ? (
                <Tooltip content={translate("llm.prompts.column.latest_citation_status.cell_tooltip")}>
                    <IconCell
                        type="swReactIcon"
                        iconName="checked-circle"
                        iconColor={colorsPalettes.carbon[400]}
                        size="xs"
                    />
                </Tooltip>
            ) : null,
    },
    {
        dataIndex: COLUMN_KEYS.citationGap,
        title: translate("llm.prompts.column.citation_gap.title"),
        tooltipSubtitle: translate("llm.prompts.column.citation_gap.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.citationGap ? sorting.sortDirection : undefined,
        sorter: citationGapStatusSorter,
        width: 150,
        hidden: true,
        render: (citationGap?: CitationGapStatus | null) => (
            <StatusIntent
                config={getCitationGapStatusConfig()}
                status={citationGap}
                data-automation="prompts-citation-gap"
            />
        ),
    },
    {
        dataIndex: COLUMN_KEYS.citationVolatility,
        title: translate("llm.prompts.column.citation_volatility.title"),
        tooltipSubtitle: translate("llm.prompts.column.citation_volatility.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.citationVolatility ? sorting.sortDirection : undefined,
        sorter: volatilityStatusSorter,
        width: 150,
        hidden: true,
        render: (citationVolatility) => (
            <StatusIntent
                config={getVolatilityStatusConfig()}
                status={citationVolatility as VolatilityStatus}
                data-automation="prompts-citation-volatility"
            />
        ),
    },
    {
        dataIndex: COLUMN_KEYS.topics,
        title: translate("llm.prompts.column.topics.title"),
        tooltipSubtitle: translate("llm.prompts.column.topics.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.topics ? sorting.sortDirection : undefined,
        sorter: topicsSort,
        width: 150,
        render: (topics: string[]) =>
            topics?.[0] ? (
                <span title={topics?.[0]}>
                    <IntentCell label={topics[0]} />
                </span>
            ) : (
                NO_DATA_SIGN
            ),
    },
    /*{
        dataIndex: COLUMN_KEYS.intent,
        title: translate("llm.prompts.column.intent.title"),
        tooltipSubtitle: translate("llm.prompts.column.intent.tooltip"),
        sortOrder: sorting.dataIndex === COLUMN_KEYS.intent ? sorting.sortDirection : undefined,
        sorter: tableSimpleSort(COLUMN_KEYS.intent),
        width: 150,
        render: (intent: string) =>
            intent ? (
                <span title={intent}>
                    <IntentCell label={intent} />
                </span>
            ) : (
                NO_DATA_SIGN
            ),
    },*/
];
